import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  StripeCardCvcElement,
  StripeCardElementOptions,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElements,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { StripeService } from '../../../services/stripe.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { SvgComponent } from '../../../shared/components/svg/svg.component';

@Component({
  selector: 'app-card-form',
  templateUrl: './card-form.component.html',
  styleUrls: ['./card-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, InputFieldComponent, InputPostDirective, SvgComponent],
})
export class CardFormComponent implements OnInit {
  private stripeService = inject(StripeService);
  private analytics = inject(PICFLOW_ANALYTICS);

  @Output() onSubmit = new EventEmitter();
  @Input() showErrorSpacing: boolean;

  cardNumber: StripeCardNumberElement;
  cardExpiry: StripeCardExpiryElement;
  cardCvc: StripeCardCvcElement;
  elements: StripeElements;

  cardNumberFocused = signal(false);
  cardNumberEmpty = signal(true);
  cardNumberError = signal<string>(undefined);

  cardExpiryFocused = signal(false);
  cardExpiryEmpty = signal(true);
  cardExpiryError = signal<string>(undefined);

  cardCvcFocused = signal(false);
  cardCvcEmpty = signal(true);
  cardCvcError = signal<string>(undefined);

  formSubmitted = signal(false);

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        fontWeight: 'normal',
        fontFamily: 'AkkuratLL, Helvetica Neue, Helvetica, Arial, sans-serif',
        fontSize: '18px',
        '::placeholder': {
          color: 'transparent',
        },
      },
      invalid: {
        color: '#31325F',
      },
    },
  };

  elementsOptions: StripeElementsOptionsClientSecret;
  cardBrand = signal<'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'unionpay' | 'unknown'>(
    undefined
  );

  elementClasses = {
    focus: 'focused',
    empty: 'empty',
    invalid: 'ng-invalid',
  };

  async ngOnInit() {
    this.elementsOptions = {
      locale: 'en',
      fonts: [
        {
          src: 'url("' + window.location.origin + '/assets/external/subset-e0b619674400d7387d042996f58c9e54.woff2")',
          family: 'AkkuratLL',
          weight: 'bold',
          style: 'normal',
        },
        {
          src: 'url("' + window.location.origin + '/assets/external/subset-2d9d98a25b1128f89314b16b8937e605.woff2")',
          family: 'AkkuratLL',
          weight: 'normal',
          style: 'normal',
        },
      ],
    };

    this.elements = await this.stripeService.elements(this.elementsOptions);
    // Only mount the element the first time
    if (!this.cardNumber) {
      this.cardNumber = this.elements.create('cardNumber', this.cardOptions);
      this.cardNumber.mount('#cardnumber');
    }
    if (!this.cardExpiry) {
      this.cardExpiry = this.elements.create('cardExpiry', this.cardOptions);
      this.cardExpiry.mount('#cardexpiry');
    }
    if (!this.cardCvc) {
      this.cardCvc = this.elements.create('cardCvc', this.cardOptions);
      this.cardCvc.mount('#cardcvc');
    }

    this.cardNumber.on('focus', () => {
      this.cardNumberFocused.set(true);
      this.onFieldFocus('number');
    });
    this.cardNumber.on('blur', () => this.cardNumberFocused.set(false));
    this.cardNumber.on('change', e => {
      this.cardBrand.set(e.empty ? undefined : e.brand);
      this.cardNumberError.set(e.error?.message || undefined);
      this.cardNumberEmpty.set(e.empty);
    });

    this.cardExpiry.on('focus', () => {
      this.cardExpiryFocused.set(true);
      this.onFieldFocus('expiry');
    });
    this.cardExpiry.on('blur', () => this.cardExpiryFocused.set(false));
    this.cardExpiry.on('change', e => {
      this.cardExpiryError.set(e.error?.message || undefined);
      this.cardExpiryEmpty.set(e.empty);
    });

    this.cardCvc.on('focus', () => {
      this.cardCvcFocused.set(true);
      this.onFieldFocus('cvc');
    });
    this.cardCvc.on('blur', () => this.cardCvcFocused.set(false));
    this.cardCvc.on('change', e => {
      this.cardCvcError.set(e.error?.message || undefined);
      this.cardCvcEmpty.set(e.empty);
    });
  }

  submit() {
    this.formSubmitted.set(true);
    this.onSubmit.emit();
  }

  public anyIssues() {
    return (
      this.cardNumberError() ||
      this.cardExpiryError() ||
      this.cardCvcError() ||
      this.cardNumberEmpty() ||
      this.cardExpiryEmpty() ||
      this.cardCvcEmpty()
    );
  }

  reportedFields = new Set<string>();
  onFieldFocus(field: string) {
    if (this.reportedFields.has(field)) return;
    this.reportedFields.add(field);
    this.analytics.track('card_form_field_focus', false, { field });
  }
}
